import React, { forwardRef, useEffect, useState } from "react";
import {
  Checkbox,
  Popover,
  FormControlLabel,
  useTheme,
  Button,
  CircularProgress,
  useMediaQuery,
  Grow,
  Slide,
  Typography,
  Divider,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Input,
  IconButton,
} from "@mui/material";
import {
  GetApp,
  Lock,
  Warning,
  ArrowRightAlt,
  Block,
  CheckCircle,
  Close,
  DragIndicator,
  ErrorOutline,
  Image,
  PriorityHigh,
  Schedule,
} from "@mui/icons-material";
import { Parser } from "json2csv";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { URLS } from "../../apis/urls";
import { downloadFileFromBlob } from "../../_helpers";
import {
  arrayMove,
  SortableContainer,
  SortableElement,
} from "react-sortable-hoc";
import clsx from "clsx";
import { QRCodeSVG } from "qrcode.react";
import { fontSizeConstant } from "../../_constants/grid.constants";
import { makeStyles } from "@mui/styles";

const { webApiUrl } = window.appConfig;

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ColumnsSelectorV2 = ({
  rowsSelected,
  type,
  onClose,
  anchorEl,
  title,
  getData,
  columns,
  rows,
}) => {
  const { t } = useTranslation();
  const [printLoading, setPrintLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const classes = useStyles();
  const [displayedColumns, setDisplayedColumns] = useState([]);
  const [selectedColumnsNames, setSelectedColumnsNames] = useState([]);
  const muiTheme = useTheme();
  const matchWidthDownSm = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [defaultFontSize, setDefaultFontSize] = useState(10);
  const printStyle = usePrintStyles({ defaultFontSize });


  useEffect(() => {
    if (anchorEl) {
      const data = columns.reduce((p, c) => {
        if (
          type &&
          c &&
          c[type?.split("-")?.[0]] !== false
        )
          return [...p, c];
        return p;
      }, []);
      setDisplayedColumns(data);
      setSelectedColumnsNames(
        data.reduce((p, c) => (c ? [...p, c.field] : p), [])
      );
    }
  }, [anchorEl]);

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const selectedIds = Array.from(rowsSelected?.ids)
      let modifiedData = await getData({
        selectedColumnsNames,
        isAllSelected:rowsSelected?.type === "include" && selectedIds.length === 0,
        idsToExport :rowsSelected?.type === "include" ? selectedIds : rows.filter(r => !selectedIds.includes(r.id)).map(r => r.id)
      });  
      const indexOfMaxIconsInData = modifiedData.reduce(
        (p, c, ind) => {
          return (c?.overdue && p >= c?.overdue?.length) || (c?.isRequiredHandling && p >= c?.isRequiredHandling?.length) ||
            (c?.extraInformation && p >= c?.extraInformation?.length)
            ? p
            : ind
        },
        0
      );
      const printedColumns = displayedColumns.filter((a) =>
        selectedColumnsNames.includes(a.field)
      );
      const hasQrColumn = selectedColumnsNames.includes("qrCode");
      const table = renderToStaticMarkup(
        <table style={printStyle.tableContainer} className={`print-table${hasQrColumn ? " qr-print" : ""}`}>
          {
            <caption style={printStyle.caption}>
              <img
                style={printStyle.companyCaptionLogo}
                src={`${webApiUrl}api/Config/WebLogo`}
                alt="company-logo"
              />
            </caption>
          }
          {hasQrColumn && (
            <colgroup>
              {printedColumns.map((c) =>
                c.field === "qrCode"
                  ? <col key={c.field} style={{ width: "88px" }} />
                  : <col key={c.field} />
              )}
            </colgroup>
          )}
          <thead>
            <tr>
              {printedColumns.map((c, j) => {
                if (c?.print !== false) {
                  if (
                    ["isRequiredHandling", "overdue", "extraInformation"].includes(c?.field)

                  ) {
                    return (
                      <th key={`th-${j}`} style={printStyle.tableHeadCell}>
                        {c.renderHeader ? (
                          <div
                            style={{
                              ...printStyle.iconHeadCell,
                              height: defaultFontSize,
                            }}
                            className="print-icon-header"
                          >
                            <c.renderHeader />
                          </div>
                        ) : (
                          <div style={printStyle?.iconHeadCell}>
                            {modifiedData?.[indexOfMaxIconsInData]?.[c?.field] &&
                              modifiedData?.[indexOfMaxIconsInData]?.[c?.field].map(
                                (ic) => {
                                  switch (ic.icon) {
                                    case "PriorityHigh": {
                                      return (
                                        <PriorityHigh
                                          style={printStyle.priorityIcon}
                                        />
                                      );
                                    }
                                    case "Schedule": {
                                      return (
                                        <Schedule
                                          style={printStyle.headCellIcon}
                                        />
                                      );
                                    }
                                    default:
                                      break;
                                  }
                                }
                              )}
                          </div>
                        )}
                      </th>
                    );
                  } else if (
                    c.field.toLowerCase().includes("attachment") ||
                    c.field.toLowerCase().includes("image")
                  )
                    return (
                      <th
                        key={`th-${j}`}
                        style={{
                          ...printStyle.tableHeadCell,
                          textAlign: "center",
                        }}
                      >
                        <Image style={printStyle.headCellIcon} />
                      </th>
                    );
                  return (
                    <th key={`th-${j}`} style={printStyle.tableHeadCell}>
                      {t(c.headerName)}
                    </th>
                  );
                }
                return null;
              })}
            </tr>
          </thead>
          <tbody>
            {modifiedData &&
              modifiedData.map((p, i) => {

                return (
                  <tr key={`table-row-${i}`}>
                    {printedColumns.map((c, j) => {
                      if (c?.print !== false) {
                        switch (typeof p[c.field]) {
                          case "object": {
                            if (
                              c.field.toLowerCase().includes("attachment") ||
                              c.field.toLowerCase().includes("image")
                            )
                              return (
                                <td
                                  key={`table-cell-${i}-${j}`}
                                  style={printStyle.priorityIcons}
                                >
                                  {p[c.field]?.length ? (
                                    <img
                                      style={printStyle.image}
                                      alt="print-img"
                                      src={
                                        c.field === "assignmentAttachments"
                                          ? URLS.GET_ASSIGNMENT_ATTACHMENT_THUMBNAIL +
                                          p[c.field][0].id
                                          : URLS.GET_ATTACHMENT_THUMBNAIL +
                                          p[c.field][0].attachmentId
                                      }
                                    />
                                  ) : (
                                    <Image style={printStyle.headCellIcon} />
                                  )}
                                </td>
                              );
                            else if (
                              ["isRequiredHandling", "overdue", "extraInformation","hasPriority","hasError","hasDownload","isCracked"].includes(c?.field)
                            ) {
                              return (
                                <td
                                  key={`table-cell-${i}-${j}`}
                                  style={printStyle.tableBodyCell}
                                >
                                  <div style={printStyle.iconHeadCell}>
                                    {p[c.field].map((ic) => {
                                      switch (ic.icon) {
                                        case "PriorityHigh": {
                                          return (
                                            <PriorityHigh
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={printStyle.priorityIcon}
                                            />
                                          );
                                        }
                                        case "Schedule": {
                                          return (
                                            <Schedule
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "ErrorOutline": {
                                          return (
                                            <ErrorOutline
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "CheckCircle": {
                                          return (
                                            <CheckCircle
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "Block": {
                                          return (
                                            <Block
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "GetApp": {
                                          return (
                                            <GetApp
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "Lock": {
                                          return (
                                            <Lock
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "Warning": {
                                          return (
                                            <Warning
                                              key={`table-cell-${i}-${j}-ic`}
                                              style={{
                                                ...printStyle.priorityIcon,
                                                ...printStyle[ic.color],
                                              }}
                                            />
                                          );
                                        }
                                        case "Message": {
                                          return (
                                            !!ic.count && (
                                              <span
                                                key={`table-cell-${i}-${j}-ic`}
                                                style={printStyle.priorityIcon}
                                              >
                                                {ic.count}
                                              </span>
                                            )
                                          );
                                        }
                                        default:
                                          break;
                                      }
                                    })}
                                  </div>
                                </td>
                              );
                            } else if (Array.isArray(p[c.field])) {
                              return (
                                <td
                                  key={`table-cell-${i}`}
                                  style={printStyle.tableBodyCell}
                                >
                                  <div style={printStyle.resolversContainer}>
                                    {p[c.field].map((r, k) => {
                                      return (
                                        <span key={`table-cell-${i}-${j}-${k}`}>
                                          {typeof r === "object" ? r.field : r}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={`table-cell-${i}`}
                                style={printStyle.tableBodyCell}
                              ></td>
                            );
                          }
                          default: {
                            if (c.field === "priority")
                              return (
                                <td
                                  key={`table-cell-${i}`}
                                  style={printStyle.priorityIcons}
                                >
                                  {p[c.field] === 2 ? (
                                    <ArrowRightAlt
                                      style={printStyle.rotate45AntiClockwise}
                                    />
                                  ) : p[c.field] === 1 ? (
                                    <ArrowRightAlt
                                      style={printStyle.rotate0Clockwise}
                                    />
                                  ) : p[c.field] === 0 ? (
                                    <ArrowRightAlt
                                      style={printStyle.rotate45Clockwise}
                                    />
                                  ) : (
                                    ""
                                  )}
                                </td>
                              );
                            if (c.field === "firstFileId")
                              return (
                                <td
                                  key={`table-cell-${i}-${j}`}
                                  style={printStyle.priorityIcons}
                                >
                                  {p[c.field]?.length ? (
                                    <img
                                      style={printStyle.image}
                                      alt="print-img"
                                      src={
                                        URLS.GET_ATTACHMENT_THUMBNAIL +
                                        p[c.field]
                                      }
                                    />
                                  ) : (
                                    <Image style={printStyle.headCellIcon} />
                                  )}
                                </td>
                              );
                            if (c.field === "qrCode" && p[c.field])
                              return (
                                <td
                                  key={`table-cell-${i}-${j}`}
                                  className="qr-cell"
                                  style={{ ...printStyle.tableBodyCell, padding: 4, textAlign: "center" }}
                                >
                                  <QRCodeSVG value={p[c.field]} size={80} />
                                </td>
                              );
                            return (
                              <td
                                key={`table-cell-${i}`}
                                style={printStyle.tableBodyCell}
                              >
                                <span>{p[c.field]}</span>
                              </td>
                            );
                          }
                        }
                      }
                      return null;
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      );

      const iframe = document.createElement("iframe");
      const link = document.createElement("link");
      link.href = "./print.css";
      link.rel = "stylesheet";
      link.type = "text/css";
      document.body.appendChild(iframe);
      iframe.style.display = "none";

      iframe.onload = function () {
        iframe.focus();
        // for other browsers
        iframe.contentDocument.head.appendChild(link);
        iframe.contentDocument.body.innerHTML = table;
      };

      // for chrome browsers
      iframe.contentDocument.head.appendChild(link);
      iframe.contentDocument.body.innerHTML = table;

      setTimeout(() => {
        iframe.contentWindow.print();
        setPrintLoading(false);
      }, 2000);
    } catch (er) {
      console.log(er, "error occured");
      setPrintLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const selectedIds = Array.from(rowsSelected?.ids)
      let modifiedData = await getData({
        selectedColumnsNames,
        isAllSelected:  rowsSelected?.type === "include" && selectedIds.length === 0,
        idsToExport :rowsSelected?.type === "include" ? selectedIds : rows.filter(r => !selectedIds.includes(r.id)).map(r => r.id)
      });    
      const printedColumns = displayedColumns.filter((a) =>
        selectedColumnsNames.includes(a.field)
      );

      const data = modifiedData?.map((c, i) => {
        return printedColumns.reduce((p, curr) => {
          if (curr.download !== false) {

            // Handle dot notation for nested properties
            const getValueFromDotNotation = (obj, field) => {
              return field.split('.').reduce((acc, part) => acc && acc[part], obj);
            };
            return {
              ...p,
              [curr.headerName]:  curr.field === "priority" && c[curr.field]
              ? t("repairRequest.priority." + c[curr.field])
              : Array.isArray(c[curr.field])
                ? c[curr.field].join(", ")
                : curr.field?.includes(".") ? getValueFromDotNotation(c, curr.field) : c[curr.field]
            };
          }

          return p;
        }, {});
      });


      if (data) {
        const parser = new Parser({
          fields: printedColumns.reduce(
            (p, c) => (c?.download !== false ? [...p, c.headerName] : p),
            []
          ),
        });
        const csv = parser.parse(data);
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        await downloadFileFromBlob(
          blob,
          `${t(title) || t("general.export")}.csv`
        );
      }

      setDownloadLoading(false);
    } catch (er) {
      console.log(er, "er");
      setDownloadLoading(false);
    }
  };

  const handleClick = () => {
    if (selectedColumnsNames.length) {
      if (type === "print") handlePrint();
      else if (type === "download-csv") handleDownload();
      else return;
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedOrderColumns = arrayMove(
      Object.assign([], displayedColumns),
      oldIndex,
      newIndex
    );
    if (oldIndex !== newIndex) setDisplayedColumns(updatedOrderColumns);
  };

  const handleChange = (e) => {
    const data = Object.assign([], selectedColumnsNames);
    const index = data.findIndex((a) => a === e.target.name);
    if (index >= 0) data.splice(index, 1);
    else data.push(e.target.name);
    setSelectedColumnsNames(data);
  };

  const handleChangeAll = (e) => {
    if (selectedColumnsNames?.length === displayedColumns?.length)
      setSelectedColumnsNames([]);
    else setSelectedColumnsNames(displayedColumns.map((a) => a.field));
  };
  const SortableItem = SortableElement(({ column, itemIndex, ...rest }) => (
    <Grid
      className={classes.listItem}
      id={`draggableItem-${column.field}`}
      size={{xs:6}}>
      <DragIndicator className={classes.dragIndicator} />
      <span className={classes.orderText}>
        {(itemIndex + 1).toString().padStart(2, "0")}
      </span>
      <FormControlLabel
        color="primary"
        className={classes.formLabel}
        classes={{ label: classes.formLabelContent }}
        control={
          <Checkbox
            onChange={handleChange}
            checked={selectedColumnsNames.includes(column.field)}
            color="primary"
            name={column.field}
          />
        }
        label={
          column?.headerName?.trim() ? (
            column?.headerName
          ) : column?.renderHeader ? (
            <column.renderHeader />
          ) : (
            column.field
          )
        }
      />
    </Grid>
  ));

  const SortableList = SortableContainer(({ columnsData }) => {
    return (
      <Grid container className={classes.gridContainer}>
        {columnsData.map((column, index) => (
          <SortableItem
            index={index}
            key={`${column.field}`}
            itemIndex={index}
            column={column}
          />
        ))}
      </Grid>
    );
  });

  const handleChangeFontSize = ({ target: { value } }) => {
    setDefaultFontSize(value);
  };

  const selectedAllColumns =
    selectedColumnsNames?.length === displayedColumns?.length;

  return (
    (<Popover
      anchorReference="anchorPosition"
      anchorPosition={{ top: 150, left: 400 }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={!!anchorEl}
      onClose={onClose}
      TransitionComponent={matchWidthDownSm ? Transition : Grow}
      className={classes.popover}
    >
      <div className={classes.popoverRoot}>
        {matchWidthDownSm ? (
          <div className={classes.stickyHeader}>
            <div className={classes.drawerHeader}>
              <div
                className={clsx(classes.titleContent, classes.maxWidth50Per)}
              >
                <Checkbox
                  className={classes.smallDeviceAllCheckbox}
                  indeterminate={
                    selectedColumnsNames?.length && !selectedAllColumns
                  }
                  onChange={handleChangeAll}
                  checked={selectedAllColumns}
                  color="primary"
                />
                <Typography className={classes.drawerTitle}>{`${t(
                  "columns.for",
                  { columnLabel: t(title) }
                )}`}</Typography>
              </div>
              <div className={classes.titleContent}>
                {type === "print" && (
                  <>
                    <label
                      className={classes.fontSizeLabel}
                      htmlFor="font-size-selector"
                    >
                      {t("print.fontsize")}
                    </label>
                    <FormControl className={classes.formControl}>
                      <Select
                        id="font-size-selector"
                        value={defaultFontSize}
                        onChange={handleChangeFontSize}
                        input={<Input />}
                      >
                        {fontSizeConstant.map((a) => (
                          <MenuItem key={a} value={a}>
                            {a}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <IconButton
                  onClick={onClose}
                  className={classes.drawerCloseIcon}
                  size="large"
                >
                  <Close />
                </IconButton>
              </div>
            </div>
            <Divider />
          </div>
        ) : (
          <Grid container className={classes.titleContent}>
            <Grid size={{xs: 6}}>
              <FormControlLabel
                color="primary"
                className={classes.formLabel}
                control={
                  <Checkbox
                    indeterminate={
                      selectedColumnsNames?.length && !selectedAllColumns
                    }
                    onChange={handleChangeAll}
                    checked={selectedAllColumns}
                    color="primary"
                  />
                }
                label={t(
                  selectedAllColumns
                    ? "columns.unselect.all"
                    : "columns.select.all"
                )}
              />
            </Grid>
            {type === "print" && (
              <Grid size={{xs: 6}}>
                <div className={classes.titleContent}>
                  <label
                    className={classes.fontSizeLabel}
                    htmlFor="font-size-selector"
                  >
                    {t("print.fontsize")}
                  </label>
                  <FormControl className={classes.formControl}>
                    <Select
                      id="font-size-selector"
                      value={defaultFontSize}
                      onChange={handleChangeFontSize}
                      input={<Input />}
                    >
                      {fontSizeConstant.map((a) => (
                        <MenuItem key={a} value={a}>
                          {a}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </Grid>
            )}
            <IconButton
              onClick={onClose}
              className={classes.drawerCloseIcon}
              size="large"
            >
              <Close />
            </IconButton>
          </Grid>
        )}
        <SortableList
          transitionDuration={400}
          columnsData={displayedColumns}
          axis="xy"
          distance={10}
          onSortEnd={onSortEnd}
        />
        <Button
          disabled={selectedColumnsNames.length === 0}
          className={classes.actionButton}
          onClick={handleClick}
          variant="outlined"
          color="primary"
        >
          {printLoading || downloadLoading ? (
            <CircularProgress size={18} />
          ) : type === "print" ? (
            t("general.print")
          ) : type === "download-csv" ? (
            t("datatable.label.toolbar.downloadCsv")
          ) :''}
        </Button>
      </div>
    </Popover>)
  );
}

export default ColumnsSelectorV2

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 80,
    maxWidth: 200,
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    background: theme.palette.common.white,
    zIndex: 1,
  },
  drawerHeader: {
    display: "flex",
    padding: theme.spacing(1.5),
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
  },
  maxWidth50Per: {
    maxWidth: "50%",
  },
  drawerTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: theme.palette.common.black,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    [theme.breakpoints.only("xs")]: {
      fontSize: 14,
    },
    [theme.breakpoints.only("sm")]: {
      fontSize: 16,
    },
  },
  drawerCloseIcon: {
    padding: theme.spacing(0.5),
    "& > span > svg": {
      fill: theme.palette.common.white,
      fontSize: 16,
    },

    [theme.breakpoints.only("xs")]: {
      marginLeft: 10,
    },
    [theme.breakpoints.up("sm")]: {
      position: "absolute",
      right: 8,
    },
  },
  popover: {
    "& .MuiPopover-paper": {
      [theme.breakpoints.only("xs")]: {
        top: "auto !important",
        bottom: "0px !important",
        left: "0px !important",
        maxWidth: "100% !important",
        width: "100%",
        boxShadow: theme.shadows[24],
        borderRadius: 20,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
  },
  popoverRoot: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(1),
  },
  columnsContainer: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    maxHeight: "70vh",
    overflow: "auto",
  },
  actionButton: {
    margin: "auto",
    marginBottom: theme.spacing(1),
  },
  formLabel: {
    margin: 0,
    marginLeft: -5,
    width: "90%",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  fontSizeChanger: {
    width: 50,
    "& input": {
      padding: 0,
    },
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
  fontSizeLabel: {
    whiteSpace: "nowrap",
    margin: 0,
    marginRight: 8,
  },
  formLabelContent: {
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  listItem: {
    margin: `0 !important`,
    opacity: `1 !important`,
    zIndex: 1500,
    display: "flex",
    fontSize: 12,
    alignItems: "center",
  },
  dragIndicator: {
    width: 12,
    height: 12,
    fontSize: 12,
    cursor: "grab",
  },
  orderText: {
    fontSize: 12,
    lineHeight: "12px",
  },
  gridContainer: {
    width: 500,
    maxWidth: "100%",
    padding: theme.spacing(1),
  },
  smallDeviceAllCheckbox: {
    padding: theme.spacing(0.5),
  },
}));

const usePrintStyles = ({ defaultFontSize, ...rest }) => {
  const theme = useTheme();
  return {
    tableContainer: {
      borderSpacing: 0,
      width: "100%",
      fontSize: defaultFontSize,
    },
    tableHeadCell: {
      textAlign: "left",
      width: 40,
      margin: 0,
      padding: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    iconHeadCell: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    priorityIcon: {
      fill: theme.palette.error.main,
      height: defaultFontSize,
      width: defaultFontSize,
      fontSize: defaultFontSize,
    },
    image: {
      width: defaultFontSize,
      objectFit: "cover",
    },
    error: {
      fill: theme.palette.error.main,
    },
    primary: {
      fill: theme.palette.primary.main,
    },
    warning: {
      fill: theme.palette.warning.main,
    },
    success: {
      fill: theme.palette.success.main,
    },
    priorityIcons: {
      textAlign: "center",
      margin: 0,
      padding: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      fontWeight: "normal",
    },
    headCellIcon: {
      height: defaultFontSize,
      width: defaultFontSize,
      fontSize: defaultFontSize,
    },
    tableBodyCell: {
      margin: 0,
      padding: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      fontWeight: "normal",
    },
    rotate45Clockwise: {
      height: defaultFontSize,
      width: defaultFontSize,
      fontSize: defaultFontSize,
      fill: theme.palette.primary.main,
      transform: "rotate(45deg)",
    },
    rotate0Clockwise: {
      fill: theme.palette.primary.main,
      height: defaultFontSize,
      width: defaultFontSize,
      fontSize: defaultFontSize,
    },
    rotate45AntiClockwise: {
      height: defaultFontSize,
      width: defaultFontSize,
      fontSize: defaultFontSize,
      fill: theme.palette.primary.main,
      transform: "rotate(-45deg)",
    },
    resolversContainer: {
      display: "flex",
      flexDirection: "column",
    },
    caption: {
      captionSide: "top",
      textAlign: "left",
      paddingBottom: theme.spacing(2.5),
    },
    companyCaptionLogo: {
      maxHeight: 35,
    },
    printTable: {
      backgroundColor: "red !important",
    },
  };
};